import { IS_PRODUCTION } from '@repo/common-utils';
import { useCommonRouter } from '@repo/platform';
import Script from 'next/script';
import { useCallback, useEffect, useRef } from 'react';

import { GoogleTrackingInitializeProps } from '../types';

export const MetaPixelScript = () => {
  return (
    <>
      <Script id="facebook-pixel" strategy="afterInteractive">
        {`
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/ko_KR/fbevents.js');
      `}
      </Script>
      <Script crossOrigin="anonymous" src="https://connect.facebook.net/en_US/sdk.js" strategy="afterInteractive" />
    </>
  );
};

export const TiktokPixelScript = () => {
  return (
    <Script id="tiktok-pixel" strategy="afterInteractive">
      {`
        !function (w, d, t) {
          w.TiktokAnalyticsObject = t;
          var ttq = (w[t] = w[t] || []);
          ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready'];
          ttq.setAndDefer = function (e, n) {
            e[n] = e[n] || function () {
              (e[n].q = e[n].q || []).push(arguments);
            };
          };
          for (var i = 0; i < ttq.methods.length; i++) {
            ttq.setAndDefer(ttq, ttq.methods[i]);
          }
          ttq.load = function (e, n) {
            var r = 'https://analytics.tiktok.com/i18n/pixel/events.js',
              o = n && n.partner;
            (ttq._i = ttq._i || {}), (ttq._i[e] = []), (ttq._i[e]._u = r), (ttq._t = ttq._t || {}), (ttq._t[e] = +new Date()), (ttq._o = ttq._o || {}), (ttq._o[e] = n || {});
            n = d.createElement('script');
            n.type = 'text/javascript';
            n.async = !0;
            n.src = r + '?sdkid=' + e + '&lib=' + t;
            var a = d.getElementsByTagName('script')[0];
            a.parentNode.insertBefore(n, a);
          };
        }(window, document, 'ttq');
      `}
    </Script>
  );
};

export const NaverPixelScript = () => {
  const pathname = useRef('');
  const pageViewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCount = useRef(0);
  const router = useCommonRouter();

  const sendPageView = useCallback(() => {
    if (!window.wcs || typeof window.wcs_do !== 'function') {
      if (retryCount.current < 10) {
        pageViewTimer.current = setTimeout(sendPageView, 500);
        retryCount.current += 1;
      }
      return;
    }
    pathname.current = router.pathname;
    window.wcs_do();
    if (!IS_PRODUCTION && process.env.NEXT_PUBLIC_TRACKING_DEBUG_MODE === 'true') {
      console.info('%c[NaverPixel]', 'font-weight: bold;', 'PageView', router.pathname);
    }
    pageViewTimer.current = null;
  }, [router.pathname]);

  useEffect(() => {
    if (router.pathname !== pathname.current) {
      if (pageViewTimer.current) {
        clearTimeout(pageViewTimer.current);
        pageViewTimer.current = null;
      }
      sendPageView();
    }
  }, [router.pathname, sendPageView]);

  return <Script id={'naver-pixel'} src="https://wcs.naver.net/wcslog.js" strategy="afterInteractive" />;
};

export const GoogleTrackingScript = ({ googleTrackingIds = [], initIds = [] }: GoogleTrackingInitializeProps) => {
  return (
    <>
      {googleTrackingIds.map((id: string) => {
        if (id.startsWith('G-') || id.startsWith('AW-')) {
          return <Script key={`gtag-js-${id}`} strategy="afterInteractive" src={`https://www.googletagmanager.com/gtag/js?id=${id}`} />;
        }
        if (id.startsWith('GTM-')) {
          return (
            <Script
              key={id}
              id={id}
              strategy="afterInteractive"
              dangerouslySetInnerHTML={{
                __html: `
                  (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;
                  j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
                  f.parentNode.insertBefore(j,f);
                  })(window,document,'script','dataLayer','${id}');
                `
              }}
            />
          );
        }
        return null;
      })}
      {initIds.length > 0 && (
        <Script id="gtag-init" strategy="afterInteractive">
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){window.dataLayer.push(arguments);}
            gtag('js', new Date());
            ${initIds.map((id: string) => `gtag('config', '${id}');`).join('\n')}
          `}
        </Script>
      )}
    </>
  );
};

export const KakaoPixelScript = () => {
  return <Script src="https://t1.daumcdn.net/kas/static/kp.js" />;
};
